import i18n from '../../locales/i18n';
import { report_kronos_client_error } from '../yodaClientErrorReport';

const LEGACY_UNEXPECTED_API_ERROR = 'Unexpected API error';

const BROWSER_NETWORK_ERROR_RE = /failed to fetch|networkerror when attempting to fetch|load failed|network request failed|failed to load|internet connection appears to be offline/i;

/**
 * @param {*} value
 * @returns {string|null}
 */
const asErrorString = (value) => {
    if (typeof value === 'string' && value.trim())
        return value;
    return null;
};

/**
 * Browser `fetch()` rejections (no HTTP response: timeout, CORS on 502/504, reset).
 *
 * @param {unknown} error
 * @returns {boolean}
 */
export const isBrowserNetworkError = (error) => {
    if (error?.name === 'AbortError')
        return false;
    return BROWSER_NETWORK_ERROR_RE.test(String(error?.message || error || ''));
};

/**
 * HTTP statuses that are client/auth outcomes, not unexpected server failures.
 *
 * @param {number|string|undefined|null} status
 * @returns {boolean}
 */
export const isExpectedClientHttpStatus = (status) => {
    const code = Number(status);
    return code === 400 || code === 401 || code === 403 || code === 404 || code === 409 || code === 422;
};

/**
 * Kore authorize / early-return bodies often serialize as `{"flash":[]}` with no
 * flash.error. That is not an unexpected server failure — do not open a Yoda card.
 *
 * @param {object} [json]
 * @returns {boolean}
 */
export const isEmptyFlashOnlyPayload = (json) => {
    if (!json || typeof json !== 'object' || Array.isArray(json))
        return false;

    const keys = Object.keys(json);
    if (keys.length !== 1 || keys[0] !== 'flash')
        return false;

    const flash = json.flash;
    if (Array.isArray(flash))
        return flash.length === 0;

    if (!flash || typeof flash !== 'object' || Array.isArray(flash))
        return false;

    return !asErrorString(flash.error)
        && !asErrorString(flash.error?.msg)
        && !asErrorString(flash.error?.message)
        && Object.keys(flash).length === 0;
};

/**
 * True when the JSON body has no usable business/flash message (unexpected path).
 *
 * @param {object} [json]
 * @param {string} [fallback]
 * @param {number|string|undefined|null} [status]
 * @returns {boolean}
 */
export const isUnexpectedApiErrorPayload = (json, fallback, status) => {
    if (isExpectedClientHttpStatus(status))
        return false;

    if (isEmptyFlashOnlyPayload(json))
        return false;

    const flashError = json?.flash?.error;
    const fromFlash = asErrorString(flashError)
        || asErrorString(flashError?.msg)
        || asErrorString(flashError?.message);
    if (fromFlash)
        return false;

    const fromError = asErrorString(json?.error)
        || asErrorString(json?.error?.message);
    if (fromError && !BROWSER_NETWORK_ERROR_RE.test(fromError))
        return false;

    const fromMessage = asErrorString(json?.message);
    if (fromMessage && !BROWSER_NETWORK_ERROR_RE.test(fromMessage))
        return false;

    const use_i18n_fallback = !fallback || fallback === LEGACY_UNEXPECTED_API_ERROR;
    return use_i18n_fallback;
};

/**
 * Replace a raw `Failed to fetch` TypeError with the i18n message + request id.
 * Does not open a Yoda card (offline / network).
 *
 * @param {unknown} error
 * @param {string} [requestId]
 * @returns {Error}
 */
export const errorFromNetworkFailure = (error, requestId) => {
    if (isBrowserNetworkError(error))
        return new Error(getApiErrorMessage({}, undefined, requestId));
    return error instanceof Error ? error : new Error(getApiErrorMessage({}, undefined, requestId));
};

/**
 * Resolve a user-facing API error message from a JSON body.
 *
 * @param {object} [json]
 * @param {string} [fallback]
 * @param {string} [requestId] Client X-Request-ID when known
 * @returns {string}
 */
export const getApiErrorMessage = (json, fallback, requestId) => {
    const flashError = json?.flash?.error;
    const fromFlash = asErrorString(flashError)
        || asErrorString(flashError?.msg)
        || asErrorString(flashError?.message);
    if (fromFlash)
        return fromFlash;

    const fromError = asErrorString(json?.error)
        || asErrorString(json?.error?.message);
    if (fromError && !BROWSER_NETWORK_ERROR_RE.test(fromError))
        return fromError;

    const fromMessage = asErrorString(json?.message);
    if (fromMessage && !BROWSER_NETWORK_ERROR_RE.test(fromMessage))
        return fromMessage;

    const id = requestId
        || json?.request_id
        || json?.requestId
        || json?.['x-request-id']
        || json?.['X-Request-Id']
        || json?.['X-Request-ID'];

    const use_i18n_fallback = !fallback || fallback === LEGACY_UNEXPECTED_API_ERROR;
    if (use_i18n_fallback) {
        if (id) {
            return i18n.t('common.unexpected_api_error_with_request_id', {
                requestId: id,
            });
        }
        return i18n.t('common.unexpected_api_error');
    }

    return fallback;
};

/**
 * Like getApiErrorMessage, but reports unexpected errors to Yoda and prefers Bxxxx.
 *
 * @param {object} [json]
 * @param {string} [fallback]
 * @param {string} [requestId]
 * @param {object} [reportContext] method, path, status, instance, bodySnippet, …
 * @returns {Promise<string>}
 */
export const resolveApiErrorMessage = async (json, fallback, requestId, reportContext) => {
    if (!isUnexpectedApiErrorPayload(json, fallback, reportContext?.status))
        return getApiErrorMessage(json, fallback, requestId);

    const id = requestId
        || json?.request_id
        || json?.requestId
        || reportContext?.requestId;

    let bodySnippet = reportContext?.bodySnippet;
    if (!bodySnippet && json && typeof json === 'object') {
        try {
            bodySnippet = JSON.stringify(json).slice(0, 4000);
        } catch (_e) {
            bodySnippet = undefined;
        }
    }

    const slugLabel = await report_kronos_client_error({
        ...(reportContext || {}),
        requestId: id,
        bodySnippet,
    });

    if (slugLabel) {
        return i18n.t('common.unexpected_api_error_with_card', {
            slugLabel,
        });
    }

    return getApiErrorMessage(json, fallback, id);
};

/**
 * Parse an API Response as JSON without throwing the browser's raw
 * "Failed to execute 'json' on 'Response'" message (empty body, HTML 500, …).
 *
 * @param {Response} response
 * @param {string} [requestId]
 * @param {object} [reportContext]
 * @returns {Promise<object>}
 */
export const parseApiResponseJson = async (response, requestId, reportContext) => {
    const headerId = typeof response?.headers?.get === 'function'
        ? (response.headers.get('X-Request-ID')
            || response.headers.get('x-request-id')
            || response.headers.get('X-Request-Id'))
        : null;
    const id = requestId || headerId || undefined;
    const ctx = {
        ...(reportContext || {}),
        status: reportContext?.status ?? response?.status,
        requestId: id,
    };

    let text = '';
    try {
        text = await response.text();
    } catch (_e) {
        throw new Error(await resolveApiErrorMessage({}, undefined, id, ctx));
    }

    if (!text || !String(text).trim())
        throw new Error(await resolveApiErrorMessage({}, undefined, id, ctx));

    try {
        return JSON.parse(text);
    } catch (_e) {
        throw new Error(await resolveApiErrorMessage({}, undefined, id, {
            ...ctx,
            bodySnippet: String(text).slice(0, 4000),
        }));
    }
};

/**
 * Shadow Response#json so empty / HTML / truncated bodies never surface as
 * `Failed to execute 'json' on 'Response'`.
 *
 * @param {Response} response
 * @param {string} [requestId]
 * @param {object} [reportContext]
 * @returns {Response}
 */
export const withSafeJson = (response, requestId, reportContext) => {
    if (!response || typeof response.json !== 'function')
        return response;

    const origJson = response.json.bind(response);
    response.json = async () => {
        try {
            return await origJson();
        } catch (_e) {
            const headerId = typeof response.headers?.get === 'function'
                ? (response.headers.get('X-Request-ID')
                    || response.headers.get('x-request-id')
                    || response.headers.get('X-Request-Id'))
                : null;
            const id = requestId || headerId || undefined;
            throw new Error(await resolveApiErrorMessage({}, undefined, id, {
                ...(reportContext || {}),
                status: reportContext?.status ?? response.status,
                requestId: id,
            }));
        }
    };
    return response;
};
